// src/components/NotificationsBell.tsx
/**
 * NotificationsBell
 * ------------------------------------------------------------
 * A self-contained bell button with a dropdown that ALWAYS
 * opens downward, anchored to the bell, without using <details>.
 *
 * - Reads/writes notifications to localStorage key: ui:notifications:v1
 * - Shows unread badge
 * - Dropdown is absolutely positioned `top-full right-0 mt-2`
 * - Click outside / Esc closes the dropdown
 * - Per-item actions: mark read, dismiss; bulk: mark all read, clear all
 *
 * Expected shape of a notification item (flexible — extra fields OK):
 * {
 *   id: string;                // uuid
 *   title: string;
 *   body?: string;
 *   level?: 'info'|'success'|'warning'|'error';
 *   createdAt: string;         // ISO
 *   read?: boolean;
 *   link?: string;             // optional route
 *   meta?: Record<string, any>
 * }
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellRing,
  X,
  Check,
  CheckCheck,
  Trash2,
  AlertTriangle,
  Info,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { get, set } from "@/lib/storage";
import { useNavigate } from "react-router-dom";

type NoticeLevel = "info" | "success" | "warning" | "error";

export type Notice = {
  id: string;
  title: string;
  body?: string;
  level?: NoticeLevel;
  createdAt: string; // ISO
  read?: boolean;
  link?: string;
  meta?: Record<string, any>;
};

const LS_KEY = "ui:notifications:v1";

function levelIcon(level: NoticeLevel = "info") {
  const cls =
    level === "error"
      ? "text-red-600"
      : level === "warning"
      ? "text-amber-600"
      : level === "success"
      ? "text-emerald-600"
      : "text-slate-500";
  const Icon =
    level === "error"
      ? AlertTriangle
      : level === "warning"
      ? AlertTriangle
      : level === "success"
      ? CheckCircle2
      : Info;
  return <Icon className={`w-4 h-4 ${cls}`} />;
}

function timeLabel(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

export default function NotificationsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notice[]>(() => get<Notice[]>(LS_KEY, []));
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Persist when items change
  useEffect(() => {
    set(LS_KEY, items);
  }, [items]);

  // Keep in sync across tabs/windows
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY) {
        try {
          const next = e.newValue ? (JSON.parse(e.newValue) as Notice[]) : [];
          setItems(next);
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent | PointerEvent | TouchEvent) => {
      if (!open) return;
      const t = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(t)) {
        setOpen(false);
      }
    };
    // use capture to beat other handlers
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const unread = useMemo(() => items.filter((i) => !i.read).length, [items]);

  const toggle = () => setOpen((v) => !v);

  const markRead = (id: string) =>
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));

  const dismiss = (id: string) =>
    setItems((xs) => xs.filter((x) => x.id !== id));

  const markAllRead = () =>
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));

  const clearAll = () => setItems([]);

  const onGo = (href?: string) => {
    if (!href) return;
    setOpen(false);
    nav(href);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        className="btn-secondary relative"
        title="Notifications"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {unread > 0 ? (
          <BellRing className="w-4 h-4" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] bg-[var(--brand-600)] text-white tabular-nums">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel — ALWAYS opens downward from the bell */}
      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="absolute right-0 top-full mt-2 z-50 w-[380px] max-w-[90vw] bg-white border rounded shadow-lg"
        >
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div className="font-medium">Notifications</div>
            <div className="flex items-center gap-1">
              <button
                className="btn-secondary px-2 py-1 text-xs"
                onClick={markAllRead}
                title="Mark all read"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Read all
              </button>
              <button
                className="btn-secondary px-2 py-1 text-xs"
                onClick={clearAll}
                title="Clear all"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Clear
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No notifications.</div>
          ) : (
            <ul className="max-h-96 overflow-auto">
              {items
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.createdAt || 0).getTime() -
                    new Date(a.createdAt || 0).getTime()
                )
                .map((n) => (
                  <li
                    key={n.id}
                    className="px-3 py-2 border-b last:border-b-0 hover:bg-slate-50"
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">{levelIcon(n.level)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium truncate">
                            {n.title || "Notification"}
                          </div>
                          {!n.read && (
                            <span className="badge">Unread</span>
                          )}
                        </div>
                        {n.body && (
                          <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">
                            {n.body}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{timeLabel(n.createdAt)}</span>
                        </div>

                        {n.link && (
                          <button
                            className="btn-secondary mt-2 text-xs"
                            onClick={() => onGo(n.link)}
                            title="Open"
                          >
                            <ExternalLink className="w-3.5 h-3.5 mr-1" />
                            Open
                          </button>
                        )}
                      </div>

                      <div className="flex flex-col gap-1 ml-2 shrink-0">
                        {!n.read && (
                          <button
                            className="btn-secondary px-2 py-1"
                            title="Mark read"
                            onClick={() => markRead(n.id)}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          className="btn-secondary px-2 py-1"
                          title="Dismiss"
                          onClick={() => dismiss(n.id)}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
